import { SNOWPLOW_PUBLIC_PROPERTIES_SCHEMA } from './types';

import type {
  SnowplowEventParams,
  SnowplowResolvedConfig,
  SnowplowSelfDescribingJson,
} from './types';

type LocalUserInfo = {
  member_level?: number | string;
  is_paid?: boolean | number;
};

/**
 * 安全读取 localStorage 字符串，避免隐私模式或异常存储影响业务链路。
 * @param key - localStorage key
 * @returns localStorage 中的字符串值
 */
function safeGetLocalStorage(key: string): string {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
}

/**
 * 安全解析 JSON 字符串。
 * @param value - JSON 字符串
 * @returns 解析后的对象
 */
function safeParseJson<T>(value: string): T | null {
  try {
    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
}

/**
 * 获取当前页面 URL，与神策 $url 逻辑保持一致。
 * @returns 当前完整 URL
 */
function getCurrentUrl(): string {
  try {
    if (window.vue_route?.fullPath) {
      return `${window.location.origin}${window.vue_route.fullPath}`;
    }
    return window.location.href;
  } catch {
    return '';
  }
}

/**
 * 获取神策 distinct id，兼容当前项目中可能存在的两个全局对象名。
 * @returns 神策 distinct id
 */
function getSensorsDistinctId(): string {
  try {
    const sensors = window.sensors || window.sensorsDataAnalytic201505;
    return sensors?.store?.getDistinctId?.() || sensors?.quick?.('getAnonymousID') || '';
  } catch {
    return '';
  }
}

/**
 * 读取渠道归因属性，过期渠道不再返回 UTM 字段。
 * @returns 渠道归因属性
 */
function getChannelProperties(): SnowplowEventParams {
  try {
    const latestChannel = window.latestChannelAttribute;
    if (!latestChannel) return {};
    const isValid = latestChannel.expires > Date.now() || latestChannel.expires === -1;
    return {
      pub_landing_page: latestChannel.pub_landing_page || undefined,
      pub_zl_page: isValid ? latestChannel.pub_zl_page || undefined : undefined,
      pub_zl_utm_source: isValid ? latestChannel.pub_zl_utm_source || undefined : undefined,
      pub_zl_utm_medium: isValid ? latestChannel.pub_zl_utm_medium || undefined : undefined,
      pub_zl_utm_campaign: isValid ? latestChannel.pub_zl_utm_campaign || undefined : undefined,
      pub_zl_referrer: isValid ? latestChannel.pub_zl_referrer || undefined : undefined,
    };
  } catch {
    return {};
  }
}

/**
 * 读取用户公共属性。
 * @returns 登录态、会员等级和支付状态
 */
function getUserProperties(): SnowplowEventParams {
  const token = safeGetLocalStorage('voo-token');
  const userInfo = safeParseJson<LocalUserInfo>(safeGetLocalStorage('voo-userInfo')) || {};
  return {
    pub_is_login: !!token,
    pub_member_level:
      userInfo.member_level !== undefined && userInfo.member_level !== null
        ? String(userInfo.member_level)
        : '9999',
    pub_is_paid: !!userInfo.is_paid,
  };
}

/**
 * 构建 Snowplow 公共属性 context。
 * @param config - Snowplow 运行配置
 * @returns Snowplow self-describing context
 */
export function buildSnowplowPublicContext(
  config: SnowplowResolvedConfig,
): SnowplowSelfDescribingJson {
  return {
    schema: SNOWPLOW_PUBLIC_PROPERTIES_SCHEMA,
    data: {
      pub_platform: 'Web',
      pub_client: 'Web',
      pub_platform_name: 'vooglamWeb',
      pub_env_name: config.env,
      pub_url: getCurrentUrl(),
      distinct_id: getSensorsDistinctId(),
      anonymous_id: window.sensorsDataAnalytic201505?.quick('getAnonymousID'),
      ...getUserProperties(),
      ...getChannelProperties(),
    },
  };
}
